import {Trans, useTranslation} from '@/translations/i18n'
import {Controller, useForm, useWatch} from 'react-hook-form'
import {ProjectBudgets} from '@/features/budget/types'
import {BudgetFormSchema} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import {StyledBudgetCard, StyledBudgetCardSection, StyledDeleteBudgetContent, StyledDeleteBudgetFooter} from './style'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Button} from '@/components/ui/button/Button'
import {CurrencyEuroIcon, Pencil01Icon, Save01Icon, Trash01Icon, XCloseIcon} from '@/components/ui/icon/Icon'
import {InputPrice} from '@/components/commons/input-price/InputPrice'
import {Text} from '@/components/ui/text/Text'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useStoreProjectBudget} from '../../queries/useStoreProjectBudget'
import {useUpdateProjectBudget} from '../../queries/useUpdateProjectBudget'
import {useDeleteProjectBudget} from '../../queries/useDeleteProjectBudget'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {errorHandler} from '@/utilities/helpers'
import {useState} from 'react'
import {
    Dialog,
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useTheme} from 'styled-components'

const currentYear = new Date().getFullYear()

const formatCurrency = (value: number) => {
    return Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(value)
}

export const BudgetCard = ({
    projectBudget,
    unallowedYears,
    setIsCreateMode
}: {
    projectBudget?: ProjectBudgets
    unallowedYears: number[]
    setIsCreateMode?: (isCreateMode: boolean) => void
}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const [isEditMode, setIsEditMode] = useState(!projectBudget)
    const storeProjectBudget = useStoreProjectBudget({
        onError: errorHandler,
        onSuccess: () => {
            setIsEditMode(false)
            setIsCreateMode?.(false)
        }
    })
    const updateProjectBudget = useUpdateProjectBudget({
        onError: errorHandler,
        onSuccess: () => {
            setIsEditMode(false)
            setIsCreateMode?.(false)
        }
    })
    const deleteProjectBudget = useDeleteProjectBudget({
        onError: errorHandler
    })

    const {
        control,
        reset,
        formState: {errors},
        setError,
        clearErrors,
        handleSubmit
    } = useForm<BudgetFormSchema>({
        resolver: zodResolver(BudgetFormSchema),
        defaultValues: {
            year: projectBudget?.year,
            outpatientAmount: projectBudget?.outpatientAmount?.toString() ?? '',
            houseAmount: projectBudget?.houseAmount?.toString() ?? ''
        }
    })

    const [outpatientAmount, houseAmount] = useWatch({
        control,
        name: ['outpatientAmount', 'houseAmount']
    })

    const onSubmit = handleSubmit(data => {
        const payload = {
            year: data.year,
            outpatientAmount: Number(data.outpatientAmount),
            houseAmount: Number(data.houseAmount)
        }

        if (projectBudget) {
            updateProjectBudget.mutate({
                urlParams: {
                    id: projectBudget.id
                },
                payload
            })
        } else {
            storeProjectBudget.mutate(payload)
        }
    })

    if (isEditMode) {
        const isPending = storeProjectBudget.isPending || updateProjectBudget.isPending

        return (
            <StyledBudgetCard>
                <Flexbox justify="space-between">
                    <Controller
                        control={control}
                        name="year"
                        render={({field}) => (
                            <InputSelect
                                style={{width: '120px'}}
                                label={t('commons:year')}
                                optionsEntries={Array.from({length: 5}, (_, index) => [
                                    `${currentYear + index}`,
                                    `${currentYear + index}`
                                ])}
                                onChange={value => {
                                    if (
                                        unallowedYears?.includes(Number(value)) &&
                                        Number(value) != projectBudget?.year
                                    ) {
                                        setError('year', {message: t('budget:unallowed_year_error')})
                                    } else {
                                        clearErrors('year')
                                    }
                                    field.onChange(Number(value))
                                }}
                                value={field.value?.toString()}
                            />
                        )}
                    />
                    <Flexbox>
                        <Button
                            variant="ghost"
                            size="xs"
                            disabled={Object.keys(errors).length >= 1 || isPending}
                            onClick={onSubmit}
                        >
                            {isPending ? <Spinner /> : <Save01Icon />}
                        </Button>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => {
                                reset()
                                setIsEditMode(false)
                                setIsCreateMode?.(false)
                            }}
                        >
                            <XCloseIcon />
                        </Button>
                    </Flexbox>
                </Flexbox>
                {errors.year && <InputHelpText error={errors.year.message} />}
                <Controller
                    control={control}
                    name="outpatientAmount"
                    render={({field}) => (
                        <InputPrice
                            getInputRef={field.ref}
                            label={t('budget:outpatient_amount_label')}
                            placeholder={'0,00'}
                            name="outpatientAmount"
                            value={field.value}
                            onValueChange={values => {
                                field.onChange(values.value)
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            typeIcon={<CurrencyEuroIcon size={16} color={palette.neutral[500]} />}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="houseAmount"
                    render={({field}) => (
                        <InputPrice
                            label={t('budget:house_amount_label')}
                            placeholder={'0,00'}
                            name="houseAmount"
                            value={field.value}
                            onValueChange={values => {
                                field.onChange(values.value)
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            typeIcon={<CurrencyEuroIcon size={16} color={palette.neutral[500]} />}
                        />
                    )}
                />
                <Flexbox direction="column" gap={1.5}>
                    <Text size="textXs" weight="500" palette="neutral" shade="500">
                        {t('budget:total_amount_label')}
                    </Text>
                    <Text size="textSm" weight="500" palette="neutral" shade="950">
                        {outpatientAmount && houseAmount
                            ? formatCurrency(Number(outpatientAmount) + Number(houseAmount))
                            : '-'}
                    </Text>
                </Flexbox>
            </StyledBudgetCard>
        )
    }

    return (
        <StyledBudgetCard>
            <Flexbox justify="space-between">
                <Flexbox direction="column" gap={1.5}>
                    <Text size="textSm" weight="500" palette="neutral" shade="950">
                        {projectBudget?.year}
                    </Text>
                    <Text weight="500" palette="neutral" shade="950">
                        {outpatientAmount && houseAmount
                            ? formatCurrency(Number(outpatientAmount) + Number(houseAmount))
                            : '-'}
                    </Text>
                </Flexbox>
                <Flexbox>
                    <Button variant="ghost" size="xs" onClick={() => setIsEditMode(true)}>
                        <Pencil01Icon />
                    </Button>
                    {projectBudget && projectBudget.year > currentYear && (
                        <DialogProvider>
                            <DialogDisclosure render={<Button variant="ghost" size="xs" />}>
                                <Trash01Icon />
                            </DialogDisclosure>
                            <Dialog>
                                <DialogBody gap={2}>
                                    <Text render="h1" size="textXl" weight={'500'}>
                                        {t('budget:delete_budget_title')}
                                    </Text>
                                    <StyledDeleteBudgetContent>
                                        <Trans
                                            i18nKey="budget:delete_budget_description"
                                            values={{
                                                year: projectBudget?.year,
                                                totalAmount: formatCurrency(
                                                    Number(outpatientAmount) + Number(houseAmount)
                                                )
                                            }}
                                            components={{br: <br />, b: <b />, ul: <ul />, li: <li />}}
                                        />
                                    </StyledDeleteBudgetContent>
                                </DialogBody>
                                <StyledDeleteBudgetFooter>
                                    <DialogDismiss
                                        render={<Button variant="secondary">{t('commons:cancel')}</Button>}
                                    />
                                    <Button
                                        variant="primaryDanger"
                                        onClick={() => deleteProjectBudget.mutate({urlParams: {id: projectBudget.id}})}
                                        disabled={deleteProjectBudget.isPending}
                                    >
                                        {t('commons:delete')}
                                        {deleteProjectBudget.isPending && <Spinner />}
                                    </Button>
                                </StyledDeleteBudgetFooter>
                            </Dialog>
                        </DialogProvider>
                    )}
                </Flexbox>
            </Flexbox>
            <StyledBudgetCardSection>
                <Text size="textXs" weight="500" palette="neutral" shade="600">
                    {outpatientAmount ? formatCurrency(Number(outpatientAmount)) : '-'}
                </Text>
                <div>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('commons:outpatient')}
                    </Text>
                    <Text size="textSm" weight="500" palette="neutral" shade="600">
                        {outpatientAmount && houseAmount
                            ? `${Math.round((Number(outpatientAmount) / (Number(outpatientAmount) + Number(houseAmount))) * 100)}%`
                            : '-'}
                    </Text>
                </div>
            </StyledBudgetCardSection>
            <StyledBudgetCardSection>
                <Text size="textXs" weight="500" palette="neutral" shade="600">
                    {houseAmount ? formatCurrency(Number(houseAmount)) : '-'}
                </Text>
                <div>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('commons:domiciliary')}
                    </Text>
                    <Text size="textSm" weight="500" palette="neutral" shade="600">
                        {houseAmount && outpatientAmount
                            ? `${Math.round((Number(houseAmount) / (Number(outpatientAmount) + Number(houseAmount))) * 100)}%`
                            : '-'}
                    </Text>
                </div>
            </StyledBudgetCardSection>
        </StyledBudgetCard>
    )
}
